import { useParams, Navigate } from "react-router-dom";
import { PageHero, SectionHeading } from "@/components/site/Sections";
import { CtaButton } from "@/components/site/CtaButton";
import { Reveal } from "@/components/site/Reveal";
import { FINANCING_PRODUCTS, FINANCING_STEPS, FINANCING_DISCLAIMER, IMAGES } from "@/data/content";
import { Check, Info, TrendingUp, Percent } from "lucide-react";

export default function FinancingProduct() {
  const { slug } = useParams();
  const f = FINANCING_PRODUCTS[slug];
  if (!f) return <Navigate to="/financing" replace />;

  return (
    <div>
      <PageHero eyebrow={f.eyebrow} title={f.headline} subtitle={f.intro} image={IMAGES.interior}>
        <div className="flex flex-wrap gap-6">
          <Stat icon={Percent} label="Financing" value={f.rate} />
          <Stat icon={TrendingUp} label="Terms" value={f.ltv} />
        </div>
        <div className="mt-8">
          <CtaButton to="/request-financing" size="lg">{f.cta}</CtaButton>
        </div>
      </PageHero>

      <section className="py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-start">
            <div>
              <SectionHeading eyebrow="Potential Uses" title={f.title} />
              <ul className="mt-8 space-y-3">
                {f.uses.map((u) => (
                  <li key={u} className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-700">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> {u}
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl border border-gold/25 bg-white p-8 gold-glow">
              <h3 className="font-display text-xl font-bold text-[#0B132B]">The Vertexhub Financing Process</h3>
              <div className="mt-6 space-y-5">
                {FINANCING_STEPS.map((s) => (
                  <div key={s.n} className="flex gap-4">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-gold/40 font-mono-x text-xs text-gold">{s.n}</span>
                    <div>
                      <h4 className="text-sm font-semibold text-[#0B132B]">{s.title}</h4>
                      <p className="text-xs text-slate-500">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <CtaButton to="/request-financing" className="mt-8 w-full">Request Financing Support</CtaButton>
            </div>
          </div>

          <div className="mt-12 flex items-start gap-3 rounded-xl border border-slate-200 bg-[#FAF8F1] p-5">
            <Info className="mt-0.5 h-5 w-5 shrink-0 text-slate-500" />
            <div className="space-y-2 text-xs leading-relaxed text-slate-500">
              <p>{f.disclaimer}</p>
              <p>{FINANCING_DISCLAIMER}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-[#FAF8F1] px-5 py-3">
    <Icon className="h-5 w-5 text-gold" />
    <div>
      <div className="font-mono-x text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="text-sm font-semibold text-[#0B132B]">{value}</div>
    </div>
  </div>
);
